import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import useQuery from '@/hooks/useQuery'
import { debounce } from 'throttle-debounce'
import { ExclamationCircleIcon } from '@heroicons/react/solid'
import { useGlobalState } from '@/state'
import { randomToastSuccess } from '@/shared/Toast'
import Tippy from '@tippyjs/react'
import ToolTip from '@/shared/ToolTip'
import { EmojiHappyIcon } from '@heroicons/react/solid'

import Card from '@/shared/Card'
import PrimaryButton from '@/shared/Buttons/Primary'

const OnboardingForm = () => {
  const [user, setUser] = useGlobalState('currentUser')
  let history = useHistory()
  const [timezones, setTimezones] = useState([])
  const { putpostRequest, getRequest } = useQuery()
  const urlParams = new window.URLSearchParams(window.location.search)
  console.log(urlParams)
  const isFromEventPage = urlParams.get('from_event')
  const { formState, reset, register, unregister, handleSubmit, setValue, getValues, setError } = useForm({
   defaultValues: {
      firstName: user.firstName,
      timezone: user.timezone,
      lastName: user.lastName
    }
  })

  const { isDirty, errors } = formState
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getRequest('/api/v1/static/timezones', {}, (err, jsonData) => {
      if (err) { /* handled in hook */ return }

      if (jsonData.timezones) {
        setTimezones(jsonData.timezones)
        setValue('timezone', user.timezone, { shouldDirty: false, shouldValidate: false })
      }
    })
  }, [])

  const onSubmit = debounce(300, data => {
    setLoading(true)

    putpostRequest(`/api/v1/users/${user.id}/onboard`, 'POST', { user: data }, (err, jsonData) => {
      setLoading(false)
      if (err) { // 422 code
        Object.entries(err).forEach(([key, value]) => {
          setError(key, value)
        })
        return
      }

      setUser({
        ...user,
        displayName: jsonData.user.displayName,
        needsOnboarding: false,
        timezone: getValues().timezone,
        firstName: getValues().first_name
      })

      window.location.href = '/dashboard?welcome=true'
    })
  })

  const submitText = isFromEventPage ? 'Save and explore courses' : 'Continue'

  return (
    <div className='mt-10'>
      { isFromEventPage && <>
        <div className="bg-indigo-600 my-6">
          <div className="max-w-7xl mx-auto py-3 px-3 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between flex-wrap">
              <div className="w-0 flex-1 flex items-center">
                <span className="flex p-2 rounded-lg bg-indigo-800">
                  <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                  </svg>
                </span>
                <p className="ml-3 font-medium text-white truncate"> Your RSVP to the event is registered. Check your email! </p>
              </div>
            </div>
          </div>
        </div>
      </> }
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <div className="px-4 sm:px-0">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">General</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Just a few questions to make sure you get a good experience.
            </p>
          </div>
        </div>
        <Card footer={<PrimaryButton onClick={handleSubmit(onSubmit)} loading={loading} text={submitText} />}>
          <form>
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-3 sm:col-span-1">
                <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                  <span>First Name</span>

                  <Tippy interactive={true} interactiveBorder={20} delay={100} content={<ToolTip>Required Field</ToolTip>}>
                    <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                  </Tippy>
                </label>
                <div className="mt-1 flex flex-col rounded-md shadow-sm">
                  <div className='relative'>
                    <input autoFocus type="text" className={errors.first_name ? 'errors' : ''} {...register('first_name', { required: true }) } defaultValue={user.firstName} placeholder="Happy" />
                    { errors.first_name && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                    </div> }
                  </div>
                </div>
                { errors.first_name && <div className='mt-2 text-sm text-red-600'>This field is required.</div> }
              </div>
              <div className="col-span-3 sm:col-span-1">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                  <span>Last Name</span>
                  <Tippy interactive={true} interactiveBorder={20} delay={100} content={<ToolTip>Required Field</ToolTip>}>
                    <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                  </Tippy>
                </label>
                <div className="mt-1 flex rounded-md shadow-sm flex flex-col">
                  <div className='relative'>
                    <input {...register('last_name', { required: true }) } className={errors.last_name ? 'errors' : ''} type="text" placeholder="Hedgehog" defaultValue={user.lastName}/>
                    { errors.last_name && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                    </div> }
                  </div>
                </div>
                { errors.last_name && <div className='mt-2 text-sm text-red-600'>This field is required.</div> }
              </div>
              <div className='col-span-2'>
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Preferred Timezone
                  </label>
                  <select
                    {...register('timezone')}
                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-cccpurple focus:border-cccpurple sm:text-sm"
                  >
                    { timezones.map((tz) => (
                      <option key={tz.computer} value={tz.computer}>{tz.human.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
              { user.needsPassword && (
                <div className="md:col-span-2 col-span-3">
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
                    <span>Set a Password</span>
                    <Tippy interactive={true} interactiveBorder={20} delay={100} content={<ToolTip>Minimum 8 characters</ToolTip>}>
                      <span onClick={(e) => e.preventDefault() }><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
                    </Tippy>
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm flex flex-col">
                    <div className='relative'>
                      <input type='password' {...register('password', { required: true }) } className={errors.password ? 'errors' : ''} />
                      { errors.password && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                      </div> }
                    </div>
                  </div>
                  { errors.password && <div className='mt-2 text-sm text-red-600'>This field is required. Minimum 8 characters</div> }
                </div>
              ) }
            </div>

          </form>
        </Card>
      </div>
    </div>
  )
}

export default OnboardingForm
